(() => {
  const WESTERN_STATES = new Set([
    'AK', 'AR', 'AZ', 'CA', 'CO', 'HI', 'IA', 'ID', 'KS', 'LA', 'MN', 'MO',
    'MT', 'ND', 'NE', 'NM', 'NV', 'OK', 'OR', 'SD', 'TX', 'UT', 'WA', 'WY'
  ]);

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const escapeHTML = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  class KPStateMap {
    constructor(root) {
      this.root = root;
      this.svg = root.querySelector('[data-kp-map-svg]');
      this.tooltip = root.querySelector('[data-kp-map-tooltip]');
      this.panel = root.querySelector('[data-kp-state-panel]');
      this.routeLayer = root.querySelector('[data-kp-route-layer]');
      this.routePath = root.querySelector('[data-kp-route-path]');
      this.routeOrigin = root.querySelector('[data-kp-route-origin]');
      this.routeMotion = root.querySelector('[data-kp-route-motion]');
      this.zipForm = root.querySelector('[data-kp-zip-form]');
      this.zipInput = root.querySelector('[data-kp-zip-input]');
      this.zipResult = root.querySelector('[data-kp-zip-result]');
      this.directorySearch = root.querySelector('[data-kp-directory-search]');
      this.officialUpdates = root.querySelector('[data-kp-official-updates]');
      this.officialList = root.querySelector('[data-kp-official-list]');
      this.officialUpdated = root.querySelector('[data-kp-official-updated]');
      this.paths = Array.from(root.querySelectorAll('[data-kp-map-states] path'));
      this.guides = this.readGuides();
      this.byCode = new Map(this.guides.map((guide) => [String(guide.code || '').toUpperCase(), guide]));
      this.byName = new Map(this.guides.map((guide) => [normalize(guide.name), guide]));
      this.pathByCode = new Map();
      this.initializePaths();
      this.bindZip();
      this.bindDirectory();
      this.loadOfficialNews();
    }

    readGuides() {
      const node = this.root.querySelector('[data-kp-state-data]');
      if (!node) return [];
      try {
        const data = JSON.parse(node.textContent || '[]');
        return Array.isArray(data) ? data : [];
      } catch (error) {
        return [];
      }
    }

    initializePaths() {
      this.paths.forEach((path) => {
        const name = path.dataset.stateName || '';
        const pathCode = String(path.dataset.stateId || '').toUpperCase();
        const guide = this.byCode.get(pathCode) || this.byName.get(normalize(name));
        if (guide) {
          path.classList.add(guide.restricted ? 'is-restricted' : 'is-legal');
          path.dataset.stateCode = guide.code;
          this.pathByCode.set(String(guide.code).toUpperCase(), path);
        }

        const status = guide && guide.restricted ? 'Statewide shipping restriction' : 'Legal';
        const stateUrl = guide ? `/pages/${guide.handle}` : `/pages/kratom-in-${normalize(name).replace(/[^a-z0-9]+/g, '-')}`;
        path.setAttribute('role', 'link');
        path.setAttribute('tabindex', '0');
        path.setAttribute('aria-label', `${name}: ${status}`);

        path.addEventListener('mouseenter', (event) => this.showTooltip(event, name, status));
        path.addEventListener('mousemove', (event) => this.positionTooltip(event));
        path.addEventListener('mouseleave', () => this.hideTooltip());
        path.addEventListener('focus', () => this.showKeyboardTooltip(path, name, status));
        path.addEventListener('blur', () => this.hideTooltip());
        path.addEventListener('click', () => { window.location.href = stateUrl; });
        path.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          window.location.href = stateUrl;
        });
      });
    }

    showTooltip(event, name, status) {
      if (!this.tooltip) return;
      this.tooltip.innerHTML = `<strong>${escapeHTML(name)}</strong><br>${escapeHTML(status)}`;
      this.tooltip.hidden = false;
      this.positionTooltip(event);
    }

    showKeyboardTooltip(path, name, status) {
      if (!this.tooltip || !this.svg) return;
      const wrap = this.svg.parentElement.getBoundingClientRect();
      const box = path.getBoundingClientRect();
      this.tooltip.innerHTML = `<strong>${escapeHTML(name)}</strong><br>${escapeHTML(status)}`;
      this.tooltip.hidden = false;
      this.tooltip.style.left = `${box.left + box.width / 2 - wrap.left}px`;
      this.tooltip.style.top = `${box.top + box.height / 2 - wrap.top}px`;
    }

    positionTooltip(event) {
      if (!this.tooltip || !this.svg) return;
      const wrap = this.svg.parentElement.getBoundingClientRect();
      this.tooltip.style.left = `${event.clientX - wrap.left}px`;
      this.tooltip.style.top = `${event.clientY - wrap.top}px`;
    }

    hideTooltip() {
      if (this.tooltip) this.tooltip.hidden = true;
    }

    bindDirectory() {
      if (!this.directorySearch) return;
      const links = Array.from(this.root.querySelectorAll('[data-kp-directory-state]'));
      this.directorySearch.addEventListener('input', () => {
        const query = normalize(this.directorySearch.value);
        links.forEach((link) => {
          link.hidden = Boolean(query && !normalize(link.dataset.search).includes(query));
        });
      });
    }

    bindZip() {
      if (!this.zipForm || !this.zipInput || !this.zipResult) return;
      this.zipForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const zip = String(this.zipInput.value || '').replace(/\D/g, '').slice(0, 5);
        this.zipResult.classList.remove('is-error');

        if (zip.length !== 5) {
          this.reportZip('Enter a valid 5-digit ZIP code.', true);
          return;
        }

        this.reportZip('Locating your destination…');
        try {
          const response = await fetch(`https://api.zippopotam.us/us/${zip}`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('ZIP code not found');
          const result = await response.json();
          const place = result.places && result.places[0];
          const code = place && place['state abbreviation'];
          if (!code) throw new Error('State not found');
          this.renderRoute(String(code).toUpperCase(), place['place name'] || '', zip);
        } catch (error) {
          this.reportZip('We could not locate that ZIP code. Check it and try again.', true);
        }
      });
    }

    renderRoute(code, city, zip) {
      const guide = this.byCode.get(code);
      const destination = this.pathByCode.get(code);
      const origin = this.pathByCode.get('NV');

      if (guide && guide.restricted) {
        this.clearRoute();
        this.reportZip(`Kratom Paradise does not ship to ${city}, ${code}. A statewide shipping restriction applies.`, true);
        this.renderPanel(guide);
        return;
      }

      if (!destination || !origin) {
        this.clearRoute();
        this.reportZip(`Delivery information for ${zip} is not available yet.`, true);
        return;
      }

      const start = this.centerOf(origin);
      const end = this.centerOf(destination);
      const distance = Math.hypot(end.x - start.x, end.y - start.y);
      const lift = Math.min(150, Math.max(55, distance * 0.27));
      const controlX = (start.x + end.x) / 2;
      const controlY = Math.min(start.y, end.y) - lift;
      this.routePath.setAttribute('d', `M ${start.x} ${start.y} Q ${controlX} ${controlY} ${end.x} ${end.y}`);
      this.routeOrigin.setAttribute('cx', start.x);
      this.routeOrigin.setAttribute('cy', start.y);
      this.routeLayer.removeAttribute('hidden');
      this.routeLayer.setAttribute('aria-hidden', 'false');
      if (this.routeMotion && typeof this.routeMotion.beginElement === 'function') this.routeMotion.beginElement();

      const estimate = guide && guide.eta ? guide.eta : (WESTERN_STATES.has(code) ? 'Estimated 1 business day' : 'Estimated 1–2 business days');
      this.reportZip(`${city}, ${code}: ${estimate} by Priority Mail after fulfillment.`);
      if (guide) this.renderPanel(guide);
    }

    centerOf(path) {
      const box = path.getBBox();
      return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    }

    renderPanel(guide) {
      if (!this.panel) return;
      const status = guide.restricted ? 'Statewide shipping restriction' : 'Legal';
      const summary = guide.summary || (guide.restricted ? 'Kratom Paradise shipping is unavailable.' : 'Kratom Paradise shipping is available.');
      this.panel.classList.toggle('is-restricted', Boolean(guide.restricted));
      this.panel.innerHTML = `
        <p class="kp-map__panel-kicker">${status}</p>
        <h3>${escapeHTML(guide.name)}</h3>
        <p>${escapeHTML(summary)}</p>
        <a class="kp-map__panel-link" href="/pages/${encodeURIComponent(guide.handle)}">Read the ${escapeHTML(guide.name)} guide →</a>
      `;
    }

    reportZip(message, isError = false) {
      this.zipResult.textContent = message;
      this.zipResult.classList.toggle('is-error', isError);
    }

    clearRoute() {
      if (this.routeLayer) {
        this.routeLayer.setAttribute('hidden', '');
        this.routeLayer.setAttribute('aria-hidden', 'true');
      }
      if (this.routePath) this.routePath.setAttribute('d', '');
    }

    async loadOfficialNews() {
      if (!this.officialUpdates || !this.officialList) return;
      const endpoint = this.officialUpdates.dataset.endpoint;
      if (!endpoint) return;

      try {
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const payload = await response.json();
        const items = Array.isArray(payload.items) ? payload.items.slice(0, 4) : [];
        if (!items.length) return;

        this.officialList.innerHTML = items.map((item) => {
          let safeUrl = '';
          try {
            const parsed = new URL(String(item.url || ''));
            if (parsed.protocol === 'https:' || parsed.protocol === 'http:') safeUrl = parsed.href;
          } catch (error) {
            return '';
          }
          if (!safeUrl) return '';
          const published = item.publishedAt ? new Date(`${item.publishedAt}T12:00:00Z`) : null;
          const date = published
            ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(`${item.publishedAt}T12:00:00Z`))
            : 'Recent update';
          const ageDays = published ? (Date.now() - published.getTime()) / 86400000 : Number.POSITIVE_INFINITY;
          const isFresh = ageDays >= 0 && ageDays <= 45;
          const sourceName = String(item.sourceName || 'Official source');
          const sourceLabel = sourceName.includes('Federal Register')
            ? 'Federal Register'
            : sourceName.includes('FDA')
              ? 'FDA'
              : sourceName;
          const sourceMark = sourceLabel === 'Federal Register' ? 'FR' : sourceLabel.slice(0, 3).toUpperCase();
          return `
            <article class="kp-map__official-item${isFresh ? ' is-fresh' : ''}" role="listitem">
              <div class="kp-map__official-meta">
                <span class="kp-map__source-mark" aria-hidden="true">${escapeHTML(sourceMark)}</span>
                <p>${escapeHTML(sourceLabel)} <span aria-hidden="true">&middot;</span> ${escapeHTML(date)}</p>
                ${isFresh ? '<strong>New</strong>' : ''}
              </div>
              <h4><a href="${escapeHTML(safeUrl)}" target="_blank" rel="noopener noreferrer">${escapeHTML(item.title || 'Kratom policy update')} <span aria-hidden="true">↗</span></a></h4>
            </article>
          `;
        }).join('');

        if (!this.officialList.children.length) return;
        if (this.officialUpdated && payload.updatedAt) {
          const updated = new Date(payload.updatedAt);
          this.officialUpdated.textContent = `Last checked ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(updated)}`;
        }
        this.officialUpdates.hidden = false;
      } catch (error) {
        // The Shopify article cards remain available if the live feed is unreachable.
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-kp-state-map]').forEach((root) => {
      if (root.dataset.kpInitialized === 'true') return;
      root.dataset.kpInitialized = 'true';
      new KPStateMap(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
